import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Download, Monitor, Calendar, CheckCircle, Package } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

const VERSIONS_API = '/versions.json';

const Downloads = () => {
  const [versionData, setVersionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(VERSIONS_API)
      .then((r) => r.json())
      .then((data) => {
        if (!mounted) return;
        setVersionData(data);
      })
      .catch((e) => {
        console.error('Failed to load version data', e);
        setError('Unable to load version information');
      })
      .finally(() => mounted && setLoading(false));

    return () => { mounted = false; };
  }, []);

  const latestVersion = versionData?.latestVersion || '1.0.1';
  const downloadUrl = versionData?.downloads?.windows?.url || 'https://cultaz.com/downloads/cultaz-desktop-1.0.1.exe';
  const latestChangelog = versionData?.changelog?.[0] || null;

  return (
    <>
      <Helmet>
        <title>Downloads - Cultaz Desktop POS | Latest Version Download</title>
        <meta name="description" content="Download Cultaz Desktop POS - fast, reliable offline point-of-sale software for Nigerian retailers. Latest version with POS system, inventory management, and sales reports. Free download." />
        <meta name="keywords" content="download Cultaz, desktop POS download, offline POS software, retail software download, Nigeria POS download, point of sale download, inventory software download" />
        <meta name="author" content="Cultaz" />
        <meta property="og:title" content="Downloads - Cultaz Desktop POS" />
        <meta property="og:description" content="Download Cultaz Desktop POS - fast, reliable offline point-of-sale software for Nigerian retailers. Latest version available." />
        <meta property="og:image" content="https://cultaz.com/banners/1.png" />
        <meta property="og:url" content="https://cultaz.com/downloads" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cultaz" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Downloads - Cultaz Desktop POS" />
        <meta name="twitter:description" content="Download Cultaz Desktop POS - offline point-of-sale software for Nigerian retailers." />
        <meta name="twitter:image" content="https://cultaz.com/banners/1.png" />
        <link rel="canonical" href="https://cultaz.com/downloads" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-secondary/5 py-16 md:py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-6 mix-blend-overlay"></div>
          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center lg:text-left"
              >
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 mb-6">
                  <Monitor className="w-5 h-5 text-primary mr-2" />
                  <span className="font-body text-sm font-medium text-primary">Cultaz Desktop POS</span>
                </div>

                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                  Download Cultaz Desktop
                </h1>

                <p className="font-body text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto lg:mx-0">
                  Get the latest version of Cultaz Desktop POS - fast, reliable, and completely offline. Perfect for Nigerian retailers who need uninterrupted operations.
                </p>

                {loading ? (
                  <div className="text-muted-foreground">Loading version information...</div>
                ) : error ? (
                  <div className="text-destructive mb-8">{error}</div>
                ) : (
                  <div className="mb-8 p-6 bg-card rounded-xl border border-border">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="font-heading text-2xl font-bold text-foreground mb-1">Version {latestVersion}</div>
                        {latestChangelog?.releaseDate && (
                          <div className="font-body text-sm text-muted-foreground flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Released: {new Date(latestChangelog.releaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                        )}
                      </div>
                      <div className="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold">Latest</div>
                    </div>

                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full"
                    >
                      <Button variant="default" size="lg" iconName="Download" iconPosition="left" fullWidth>
                        Download Now (Windows)
                      </Button>
                    </a>
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border">
                  <img
                    src="/cultaz-offline.png"
                    alt="Cultaz Desktop POS on laptop"
                    className="w-full h-auto aspect-[4/3] object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* What's New Section */}
        {latestChangelog && (
          <section className="py-16 md:py-20 lg:py-24 bg-background">
            <div className="container mx-auto px-6 lg:px-12">
              <div className="max-w-3xl mx-auto">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                  What's New in Version {latestVersion}
                </h2>

                <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-purple-sm">
                  <div className="space-y-4">
                    {latestChangelog.whatsNew?.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="flex items-start gap-4"
                      >
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <p className="font-body text-base text-foreground">{feature}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Screenshots Gallery */}
        <section className="py-16 md:py-20 lg:py-24 bg-muted/30">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                See Cultaz Desktop in Action
              </h2>
              <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
                Take a look at the clean, intuitive interface that makes retail management effortless.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { src: '/desktop-app-screenshots/UI1.png', alt: 'Main POS Interface' },
                { src: '/desktop-app-screenshots/startup-page.png', alt: 'Startup Page' },
                { src: '/desktop-app-screenshots/settings-page.png', alt: 'Settings Page' },
                { src: '/desktop-app-screenshots/customer-facing-screen-detection.png', alt: 'Customer Screen Detection' },
                { src: '/desktop-app-screenshots/network-searching.png', alt: 'Network Search' },
                { src: '/desktop-app-screenshots/manual-client-connection-via-ip-address.png', alt: 'Manual Client Connection' }
              ].map((img, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-card rounded-xl overflow-hidden border border-border shadow-purple-sm hover:shadow-purple-md transition-smooth"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-56 object-cover"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/site-placeholder.svg'; }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* System Requirements */}
        <section className="py-16 md:py-20 lg:py-24 bg-background">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
                System Requirements
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl p-6 border border-border">
                  <Package className="w-8 h-8 text-primary mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">Operating System</h3>
                  <p className="font-body text-muted-foreground">Windows 10 or later</p>
                </div>

                <div className="bg-card rounded-xl p-6 border border-border">
                  <Package className="w-8 h-8 text-primary mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">Storage</h3>
                  <p className="font-body text-muted-foreground">200 MB free disk space</p>
                </div>

                <div className="bg-card rounded-xl p-6 border border-border">
                  <Package className="w-8 h-8 text-primary mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">Memory</h3>
                  <p className="font-body text-muted-foreground">2 GB RAM minimum (4 GB recommended)</p>
                </div>

                <div className="bg-card rounded-xl p-6 border border-border">
                  <Package className="w-8 h-8 text-primary mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">Network</h3>
                  <p className="font-body text-muted-foreground">Internet connection optional (for license activation)</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default Downloads;
